import { useEffect, useRef } from "react";

const DEFAULT_INACTIVITY_THRESHOLD_MS = 15 * 60 * 1000;

function getInactivityThreshold(): number {
  const configured = Number(import.meta.env.VITE_RELOAD_AFTER_INACTIVITY_MS);
  return Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_INACTIVITY_THRESHOLD_MS;
}

/**
 * Reloads the current route once when the user returns after a long absence.
 * This refreshes every data source on the active screen without polling.
 */
export function useReloadAfterInactivity(): void {
  const inactiveSinceRef = useRef<number | null>(null);
  const lastActivityAtRef = useRef(Date.now());
  const reloadingRef = useRef(false);

  useEffect(() => {
    const thresholdMs = getInactivityThreshold();

    const markInactive = () => {
      if (inactiveSinceRef.current === null) {
        inactiveSinceRef.current = Date.now();
      }
    };

    const reload = () => {
      reloadingRef.current = true;
      window.location.reload();
    };

    const refreshIfNeeded = () => {
      if (document.visibilityState === "hidden" || reloadingRef.current) return;

      const inactiveSince = inactiveSinceRef.current;
      inactiveSinceRef.current = null;
      const lastKnownActivity = inactiveSince ?? lastActivityAtRef.current;
      if (Date.now() - lastKnownActivity < thresholdMs) {
        lastActivityAtRef.current = Date.now();
        return;
      }

      reload();
    };

    const handleUserActivity = () => {
      if (reloadingRef.current) return;

      const now = Date.now();
      if (now - lastActivityAtRef.current >= thresholdMs) {
        reload();
        return;
      }

      lastActivityAtRef.current = now;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markInactive();
      } else {
        refreshIfNeeded();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", markInactive);
    window.addEventListener("focus", refreshIfNeeded);
    window.addEventListener("pagehide", markInactive);
    window.addEventListener("pageshow", refreshIfNeeded);
    window.addEventListener("pointerdown", handleUserActivity, { passive: true });
    window.addEventListener("keydown", handleUserActivity);
    window.addEventListener("touchstart", handleUserActivity, { passive: true });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", markInactive);
      window.removeEventListener("focus", refreshIfNeeded);
      window.removeEventListener("pagehide", markInactive);
      window.removeEventListener("pageshow", refreshIfNeeded);
      window.removeEventListener("pointerdown", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("touchstart", handleUserActivity);
    };
  }, []);
}
